import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { CommanServices } from '@/services/commanServices';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Library, Plus, Search, Eye, Pencil, Copy, Archive, Trash2, X,
  ChevronRight, MapPin, Building, Users, Zap, History, ClipboardList, Rocket, MoreVertical, ExternalLink, Target, ListChecks, Save, Wand2, Store,
  Layers, Flag, Crosshair, Wrench, ArrowLeft, Check, Info,
  Calendar, Gauge, Filter, XCircle, ChevronDown, Sparkles, Globe, Tag, Boxes,
} from 'lucide-react';
import { FilterField, FilterGroup, SearchSelect, FILTER_INPUT_CLS } from './ReportsCenter';
import ScenarioBuilderAgent from '@/components/ScenarioBuilderAgent';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import ExerciseScenarioEditor from '@/components/ExerciseScenarioEditor';
import { SaaSServices } from '@/services/saasServices';
import { apiErrorMessage, canManageInstitutionAccess, readStoredUser, unwrapApiData } from '@/auth/saasSession';
import { isInstitutionIncidentAdministrator } from '@/auth/incidentPermissions';
import { isFeatureEnabled, LICENSE_FEATURES } from '@/auth/featureEntitlements';

const LEVELS = ['Strategic', 'Tactical', 'Operational'];
const LEVEL_ICONS = { Strategic: Flag, Tactical: Crosshair, Operational: Wrench };

/* Create Scenario modal — light premium shell (scoped `csw-` classes). */
const CSW_STYLE = `
.csw-font { font-family: 'IBM Plex Sans Arabic', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; }
.csw-input { background: #fff; border: 1px solid #dbe0f0; border-radius: 11px; color: #0f172a; transition: border-color .15s, box-shadow .15s; }
.csw-input::placeholder { color: #a3abc4; }
.csw-input:focus { outline: none; border-color: #7c3aed; box-shadow: 0 0 0 3px rgba(124,58,237,0.12); }
.csw-primary { background: linear-gradient(135deg,#4338ca,#7c3aed); color: #fff; box-shadow: 0 1px 0 rgba(255,255,255,0.25) inset, 0 8px 24px rgba(79,70,229,0.35); transition: filter .15s, transform .15s; }
.csw-primary:hover:not(:disabled) { filter: brightness(1.08); transform: translateY(-1px); }
.csw-eyebrow { font-size: 11.5px; font-weight: 700; letter-spacing: 0.12em; text-transform: uppercase; color: #64748b; }
.csw-scroll { scrollbar-width: none; -ms-overflow-style: none; }
.csw-scroll::-webkit-scrollbar { display: none; width: 0; height: 0; }
`;

const CswPill = ({ active, Icon, label, onClick, testId }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testId}
    className="flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[12.5px] font-semibold transition-all"
    style={active
      ? { background: 'linear-gradient(135deg,#4338ca,#7c3aed)', color: '#fff',
          boxShadow: '0 1px 0 rgba(255,255,255,0.25) inset, 0 6px 18px rgba(79,70,229,0.35)', border: '1px solid transparent' }
      : { background: '#fff', color: '#475569', border: '1px solid #dbe0f0' }}
  >
    <Icon className="h-4 w-4" style={active ? undefined : { color: '#7c3aed' }} />
    {label}
  </button>
);
const STATUSES = ['Draft', 'Active', 'Archived'];
const IMPACT_OPTIONS = ['Minor', 'Moderate', 'Severe', 'Critical'];

const levelColor = (l) => ({
  Strategic: 'bg-purple-100 text-purple-700 border-purple-200',
  Tactical: 'bg-blue-100 text-blue-700 border-blue-200',
  Operational: 'bg-teal-100 text-teal-700 border-teal-200',
}[l] || 'bg-gray-100 text-gray-600 border-gray-200');

const statusColor = (s) => ({
  Draft: 'bg-amber-100 text-amber-700 border-amber-200',
  Active: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  Archived: 'bg-gray-200 text-gray-600 border-gray-300',
}[s] || 'bg-gray-100 text-gray-600');

const blankAssumption = () => ({
  id: `tmp-${Math.random().toString(36).slice(2)}`,
  title: '', description: '', location: '', department: '', impact: '', severity: '', notes: '',
});

export default function ScenarioLibrary() {
  const navigate = useNavigate();
  const routerLocation = useLocation();
  const currentUser = useMemo(() => readStoredUser(), []);
  const canManageAccess = canManageInstitutionAccess(currentUser);
  const marketplaceEnabled = isFeatureEnabled(currentUser, LICENSE_FEATURES.SCENARIO_MARKETPLACE);
  const [scenarios, setScenarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAgent, setShowAgent] = useState(false);
  const [search, setSearch] = useState('');
  const [stats, setStats] = useState({});
  const [usageKpis, setUsageKpis] = useState({ real: 0, drill: 0 });
  // Reports-Center-style filters (same bar, minus Status)
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [fFrom, setFFrom] = useState('');
  const [fTo, setFTo] = useState('');
  const [typeF, setTypeF] = useState('all');
  const [eventF, setEventF] = useState('all');
  const [triggerF, setTriggerF] = useState('all');
  const [locationF, setLocationF] = useState('all');
  const [severityF, setSeverityF] = useState('all');
  const [savedViews, setSavedViews] = useState(() => {
    try { return JSON.parse(localStorage.getItem('scenarioLibSavedViews') || '[]'); } catch { return []; }
  });

  const [wizardOpen, setWizardOpen] = useState(false);
  const [viewItem, setViewItem] = useState(null);
  const [createPrefill, setCreatePrefill] = useState(null);
  const [agentPrefill, setAgentPrefill] = useState(null);
  const [marketplacePolicy, setMarketplacePolicy] = useState(null);
  const [marketplacePublications, setMarketplacePublications] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [res, statsRes] = await Promise.all([
        CommanServices.listScenarios(),
        CommanServices.getScenarioStats().catch(() => null),
      ]);
      const d = res?.data ?? res;
      setScenarios(d?.scenarios || []);
      const st = statsRes?.data?.stats || {};
      setStats(st);
      setUsageKpis({
        real: Object.values(st).reduce((n, x) => n + (x.real_uses || 0), 0),
        drill: Object.values(st).reduce((n, x) => n + (x.drill_uses || 0), 0),
      });
    } catch (e) {
      console.error('load scenarios failed', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  const loadMarketplaceStatus = useCallback(async () => {
    if (!marketplaceEnabled) {
      setMarketplacePolicy({ enabled: false, can_publish: false });
      setMarketplacePublications([]);
      return;
    }
    if (!canManageAccess) {
      const response = await SaaSServices.listMarketplace();
      const data = unwrapApiData(response) || {};
      setMarketplacePolicy({ enabled: data.enabled === true });
      setMarketplacePublications([]);
      return;
    }
    const [policyResponse, publicationsResponse] = await Promise.all([
      SaaSServices.getMarketplacePolicy(),
      SaaSServices.listCurrentMarketplacePublications(),
    ]);
    setMarketplacePolicy(unwrapApiData(policyResponse));
    const publicationData = unwrapApiData(publicationsResponse);
    setMarketplacePublications(publicationData?.listings || []);
  }, [canManageAccess, marketplaceEnabled]);
  useEffect(() => { loadMarketplaceStatus(); }, [loadMarketplaceStatus]);

  const canCreateScenario = isInstitutionIncidentAdministrator(currentUser);
  const canPublishAtInstitutionLevel = marketplaceEnabled && isInstitutionIncidentAdministrator(currentUser);

  const [editScenario, setEditScenario] = useState(null);

  // Cascading classification options (type › event › trigger), like the Reports Center.
  const countOptions = (rows, key) => {
    const m = {};
    rows.forEach((r) => { const v = r[key]; if (v) m[v] = (m[v] || 0) + 1; });
    return Object.entries(m).sort().map(([value, count]) => ({ value, count }));
  };
  const typeOptions = useMemo(() => countOptions(scenarios, 'incident_type'), [scenarios]);
  const eventOptions = useMemo(
    () => countOptions(scenarios.filter((s) => typeF === 'all' || s.incident_type === typeF), 'event'),
    [scenarios, typeF]
  );
  const triggerOptions = useMemo(
    () => countOptions(scenarios.filter((s) => (typeF === 'all' || s.incident_type === typeF) && (eventF === 'all' || s.event === eventF)), 'trigger'),
    [scenarios, typeF, eventF]
  );
  const locationOptions = useMemo(() => {
    const m = {};
    scenarios.forEach((s) => (s.location_names || []).forEach((n) => { if (n) m[n] = (m[n] || 0) + 1; }));
    return Object.entries(m).sort().map(([value, count]) => ({ value, count }));
  }, [scenarios]);
  const severityOptions = useMemo(() => countOptions(scenarios, 'severity'), [scenarios]);
  const onTypeChange = (v) => { setTypeF(v); setEventF('all'); setTriggerF('all'); };
  const onEventChange = (v) => { setEventF(v); setTriggerF('all'); };

  const filtered = useMemo(() => {
    const fromD = fFrom ? new Date(fFrom) : null;
    const toD = fTo ? new Date(fTo + 'T23:59:59') : null;
    const q = search.trim().toLowerCase();
    return scenarios.filter((s) => {
      if (typeF !== 'all' && s.incident_type !== typeF) return false;
      if (eventF !== 'all' && s.event !== eventF) return false;
      if (triggerF !== 'all' && s.trigger !== triggerF) return false;
      if (locationF !== 'all' && !(s.location_names || []).includes(locationF)) return false;
      if (severityF !== 'all' && s.severity !== severityF) return false;
      const d = s.updated_at || s.created_at ? new Date(s.updated_at || s.created_at) : null;
      if (fromD && d && d < fromD) return false;
      if (toD && d && d > toD) return false;
      if (q && !(`${s.name} ${s.description || ''} ${s.incident_type || ''} ${s.event || ''} ${s.trigger || ''} ${(s.location_names || []).join(' ')}`.toLowerCase().includes(q))) return false;
      return true;
    });
  }, [scenarios, typeF, eventF, triggerF, locationF, severityF, fFrom, fTo, search]);

  const activeFilterCount = [
    fFrom, fTo,
    typeF !== 'all' && typeF, eventF !== 'all' && eventF, triggerF !== 'all' && triggerF,
    locationF !== 'all' && locationF, severityF !== 'all' && severityF, search.trim(),
  ].filter(Boolean).length;
  const clearFilters = () => {
    setFFrom(''); setFTo(''); setTypeF('all'); setEventF('all'); setTriggerF('all');
    setLocationF('all'); setSeverityF('all'); setSearch('');
  };
  const persistViews = (views) => {
    setSavedViews(views);
    try { localStorage.setItem('scenarioLibSavedViews', JSON.stringify(views)); } catch { /* ignore */ }
  };
  const saveCurrentView = () => {
    const name = window.prompt('Name this view:', '');
    if (!name || !name.trim()) return;
    persistViews([
      ...savedViews.filter((v) => v.name !== name.trim()),
      { name: name.trim(), fFrom, fTo, typeF, eventF, triggerF, locationF, severityF, search },
    ]);
  };
  const applyView = (v) => {
    setFFrom(v.fFrom || ''); setFTo(v.fTo || ''); setTypeF(v.typeF || 'all'); setEventF(v.eventF || 'all');
    setTriggerF(v.triggerF || 'all'); setLocationF(v.locationF || 'all'); setSeverityF(v.severityF || 'all');
    setSearch(v.search || '');
  };
  const filterChips = [
    fFrom && { key: 'from', label: `From ${fFrom}`, clear: () => setFFrom('') },
    fTo && { key: 'to', label: `To ${fTo}`, clear: () => setFTo('') },
    typeF !== 'all' && { key: 'type', label: `Type · ${typeF}`, clear: () => onTypeChange('all') },
    eventF !== 'all' && { key: 'event', label: `Event · ${eventF}`, clear: () => onEventChange('all') },
    triggerF !== 'all' && { key: 'trigger', label: `Trigger · ${triggerF}`, clear: () => setTriggerF('all') },
    locationF !== 'all' && { key: 'location', label: `Location · ${locationF}`, clear: () => setLocationF('all') },
    severityF !== 'all' && { key: 'severity', label: `Severity · ${severityF}`, clear: () => setSeverityF('all') },
    search.trim() && { key: 'search', label: `“${search.trim()}”`, clear: () => setSearch('') },
  ].filter(Boolean);

  const kpis = useMemo(() => ({
    total: scenarios.length,
    active: scenarios.filter((s) => s.status === 'Active').length,
    draft: scenarios.filter((s) => s.status === 'Draft').length,
    archived: scenarios.filter((s) => s.status === 'Archived').length,
    reusable: scenarios.filter((s) => s.original_scenario_id).length,
    ai: scenarios.filter((s) => s.origin === 'ai_agent').length,
  }), [scenarios]);

  const openCreate = () => { setEditScenario(null); setCreatePrefill(null); setWizardOpen(true); };
  const openEdit = (s) => { setEditScenario(s); setCreatePrefill(null); setWizardOpen(true); };

  // Open the create wizard pre-filled when arriving from "Build a Scenario" on Plan Exercise.
  useEffect(() => {
    const pre = routerLocation.state?.createPrefill;
    if (pre) {
      setEditScenario(null);
      setCreatePrefill(pre);
      setWizardOpen(true);
      navigate(routerLocation.pathname, { replace: true, state: {} });
    }
  }, [routerLocation.state]);

  // ForeSight opens the Scenario Builder Agent directly with the complete
  // detected-case evidence contract; it must not fall back to the manual
  // Create Scenario wizard.
  useEffect(() => {
    const pre = routerLocation.state?.agentPrefill;
    if (!pre) return;
    setAgentPrefill(pre);
    setShowAgent(true);
    navigate(routerLocation.pathname, { replace: true, state: {} });
  }, [routerLocation.state]);

  const handleDuplicate = async (s) => {
    const res = await CommanServices.duplicateScenario(s.id);
    if (res?.status !== false) { toast.success(res?.message || 'Scenario duplicated'); load(); }
    else toast.error(res?.message || 'Failed to duplicate');
  };
  const handleArchive = async (s) => {
    const res = await CommanServices.archiveScenario(s.id);
    if (res?.status !== false) { toast.success(`Scenario ${s.status === 'Archived' ? 'reactivated' : 'archived'}`); load(); }
    else toast.error('Failed to archive');
  };
  const handleDelete = async (s) => {
    if (!window.confirm(`Delete scenario "${s.name}"? This cannot be undone.`)) return;
    const res = await CommanServices.deleteScenario(s.id);
    if (res?.status !== false) { toast.success('Scenario deleted'); load(); }
    else toast.error('Failed to delete');
  };
  const handlePublish = async (scenario) => {
    if (!marketplaceEnabled) return;
    const publisherNote = window.prompt('Optional note for Marketplace reviewers:', '') ?? null;
    if (publisherNote == null) return;
    const response = await SaaSServices.publishScenario(scenario.id, { publisher_note: publisherNote });
    const listing = unwrapApiData(response);
    if (!listing) return toast.error(apiErrorMessage(response));
    setMarketplacePublications((rows) => [listing, ...rows.filter((row) => row.source_scenario_id !== scenario.id)]);
    toast.success(listing.status === 'published' ? 'Scenario published to the Marketplace' : 'Scenario submitted for platform approval');
  };
  const handleUnpublish = async (scenario) => {
    if (!marketplaceEnabled) return;
    if (!window.confirm(`Remove "${scenario.name}" from Marketplace sharing?`)) return;
    const response = await SaaSServices.unpublishScenario(scenario.id);
    if (!unwrapApiData(response)) return toast.error(apiErrorMessage(response));
    setMarketplacePublications((rows) => rows.map((row) => row.source_scenario_id === scenario.id ? { ...row, status: 'revoked' } : row));
    toast.success('Marketplace publication revoked');
  };
  const [launchingId, setLaunchingId] = useState(null);
  const [activatingId, setActivatingId] = useState(null);
  const handleLaunch = async (s) => {
    setLaunchingId(s.id);
    try {
      const res = await CommanServices.launchScenario(s.id, {});
      const inc = res?.data?.incident_id || res?.incident_id;
      if (res?.status !== false && inc) {
        toast.success('Drill created from scenario');
        navigate(`/incident-details/${inc}`);
      } else {
        toast.error(res?.message || 'Failed to start drill');
      }
    } catch (e) {
      toast.error('Failed to start drill');
    } finally {
      setLaunchingId(null);
    }
  };
  const handleActivateScenario = async (s) => {
    setActivatingId(s.id);
    try {
      const res = await CommanServices.updateScenario(s.id, { status: 'Active' });
      if (res?.status !== false) {
        toast.success('Scenario activated');
        await load();
      } else {
        toast.error(res?.message || 'Failed to activate scenario');
      }
    } catch (e) {
      toast.error('Failed to activate scenario');
    } finally {
      setActivatingId(null);
    }
  };

  return (
    <div className="h-full w-full overflow-y-auto bg-[#f8fafc] px-4 py-5 sm:px-6 lg:px-8 xl:px-10" data-testid="scenario-library-page">
      <div className="mx-auto w-full max-w-[1920px]">
        {/* Page header */}
        <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex min-w-0 flex-1 items-center gap-3">
            <span className="flex h-12 w-12 flex-none items-center justify-center rounded-xl bg-gradient-to-br from-indigo-500 to-violet-600 text-white shadow-lg shadow-indigo-200">
              <Library className="h-6 w-6" />
            </span>
            <div className="min-w-0">
              <h1 className="text-[26px] font-black tracking-[-0.035em] text-slate-950 sm:text-[28px]">Scenario Library</h1>
              <p className="max-w-2xl text-[13px] font-medium leading-snug text-slate-500 sm:text-sm">
                Build once, reuse everywhere — your central library for exercise scenarios &amp; assumptions.
              </p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2 lg:flex-none lg:flex-nowrap lg:justify-end">
            {marketplaceEnabled && marketplacePolicy?.enabled !== false && (
              <button
                type="button"
                onClick={() => navigate('/scenario-marketplace')}
                data-testid="scenario-marketplace-link"
                className="inline-flex h-10 items-center justify-center gap-1.5 whitespace-nowrap rounded-xl border border-emerald-200 bg-gradient-to-b from-emerald-50 to-white px-3.5 text-[13px] font-bold text-emerald-700 shadow-sm shadow-emerald-100/80 transition hover:border-emerald-300 hover:from-emerald-100 hover:text-emerald-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:ring-offset-2"
              >
                <Store className="h-4 w-4" /> Scenario Marketplace
              </button>
            )}
            {canCreateScenario && (
              <button
                type="button"
                onClick={openCreate}
                data-testid="create-scenario-btn"
                className="inline-flex h-10 items-center justify-center gap-1.5 whitespace-nowrap rounded-xl border border-indigo-200 bg-indigo-50 px-3.5 text-[13px] font-bold text-indigo-700 shadow-sm transition hover:border-indigo-300 hover:bg-indigo-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2"
              >
                <Plus className="h-4 w-4" /> Create Scenario
              </button>
            )}
            {canCreateScenario && (
              <button
                type="button"
                onClick={() => setShowAgent(true)}
                data-testid="scenario-builder-agent-btn"
                className="inline-flex h-10 items-center justify-center gap-1.5 whitespace-nowrap rounded-xl bg-gradient-to-r from-indigo-600 to-violet-600 px-3.5 text-[13px] font-bold text-white shadow-lg shadow-indigo-200 transition hover:-translate-y-0.5 hover:shadow-xl hover:shadow-indigo-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2"
              >
                <Wand2 className="h-4 w-4" />
                Scenario Builder
                <span className="text-[10px] font-black uppercase tracking-[0.1em] text-indigo-100">Agent</span>
              </button>
            )}
          </div>
          {showAgent && (
            <ScenarioBuilderAgent
              prefill={agentPrefill}
              onClose={() => {
                setShowAgent(false);
                setAgentPrefill(null);
              }}
            />
          )}
        </div>

        {/* Summary rail */}
        <div className="mb-7 grid grid-cols-2 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm sm:grid-cols-4 xl:grid-cols-8" data-testid="scenario-kpis">
          <Kpi label="Total" value={kpis.total} testid="kpi-total" />
          <Kpi label="Active" value={kpis.active} testid="kpi-active" />
          <Kpi label="Drafts" value={kpis.draft} testid="kpi-draft" />
          <Kpi label="Archived" value={kpis.archived} tone="muted" testid="kpi-archived" />
          <Kpi label="Reused copies" value={kpis.reusable} tone="muted" testid="kpi-reused" />
          <Kpi label="Incident uses" value={usageKpis.real} tone="accent" testid="kpi-real-uses" />
          <Kpi label="Drill uses" value={usageKpis.drill} testid="kpi-drill-uses" />
          <Kpi label="AI-generated" value={kpis.ai} testid="kpi-ai" />
        </div>

        {!canCreateScenario && !loading && (
          <div className="mb-6 rounded-xl border border-indigo-200 bg-indigo-50 px-4 py-3 text-sm text-indigo-800" data-testid="scenario-scoped-access-notice">
            You can work only with scenarios assigned to your configured incident role. Creating a new institution-wide template requires the Scenario Library permission on your institution role.
          </div>
        )}

        {/* Search, saved views and filters */}
        <div className="mb-7" data-testid="scenario-filters">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
            <div className="relative min-w-0 flex-1">
              <Search className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
            <input
              data-testid="scenario-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search name, description, type, event, trigger, location…"
                className="block h-12 w-full rounded-xl border border-slate-200 bg-white pl-12 pr-10 text-sm font-medium text-slate-900 shadow-sm outline-none transition placeholder:text-slate-400 focus:border-indigo-400 focus:ring-4 focus:ring-indigo-100"
            />
              {search && (
                <button
                  type="button"
                  onClick={() => setSearch('')}
                  className="absolute right-3 top-1/2 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-700"
                  aria-label="Clear search"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
          </div>
            <div className="flex flex-wrap items-center gap-3 lg:flex-none lg:flex-nowrap">
              <div className="min-w-[72px] text-center" data-testid="scenario-filter-count">
                <p className="text-sm font-black text-slate-900">
                  <span className="text-indigo-600">{filtered.length}</span>
                  <span className="text-xs font-bold text-slate-400"> / {scenarios.length}</span>
                </p>
            </div>
            {activeFilterCount > 0 && (
                <button type="button" onClick={clearFilters} data-testid="scenario-clear-filters" className="inline-flex h-11 items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3 text-xs font-bold text-slate-500 shadow-sm transition hover:border-rose-200 hover:text-rose-600">
                <XCircle className="w-3.5 h-3.5" />Clear all
              </button>
            )}
              <button
                type="button"
                onClick={saveCurrentView}
                data-testid="scenario-save-view"
                title="Save the current filters as a one-click view"
                className="inline-flex h-12 items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 text-sm font-bold text-slate-600 shadow-sm transition hover:border-indigo-200 hover:text-indigo-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
              >
                <Save className="h-4 w-4" />Save view
            </button>
              <button
                type="button"
                onClick={() => setFiltersOpen((v) => !v)}
                data-testid="scenario-filters-toggle"
                aria-expanded={filtersOpen}
                className={`inline-flex h-12 items-center gap-2 rounded-xl border px-4 text-sm font-bold shadow-sm transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 ${filtersOpen ? 'border-indigo-200 bg-indigo-50 text-indigo-700' : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300 hover:text-slate-900'}`}
              >
                <Filter className="h-4 w-4" />Filters
              {activeFilterCount > 0 && <span className="text-[9px] font-black px-1.5 rounded-full bg-indigo-600 text-white">{activeFilterCount}</span>}
              <ChevronDown className={`w-3.5 h-3.5 transition-transform ${filtersOpen ? 'rotate-180' : ''}`} />
            </button>
            </div>
          </div>

        {savedViews.length > 0 && (
            <div className="mt-3 flex flex-wrap items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-4 py-2.5 shadow-sm" data-testid="scenario-saved-views">
            <span className="text-[10px] font-black uppercase tracking-widest text-gray-300 mr-1">Views</span>
            {savedViews.map((view) => (
              <span key={view.name} className="group inline-flex items-center gap-1 text-[11px] font-bold text-gray-600 bg-gray-50 border border-gray-200 hover:border-indigo-300 hover:text-indigo-700 rounded-full pl-2.5 pr-1 py-0.5 transition-colors">
                  <button type="button" onClick={() => applyView(view)} title="Apply this view">{view.name}</button>
                  <button type="button" onClick={() => persistViews(savedViews.filter((v) => v.name !== view.name))} title="Delete view" aria-label={`Delete saved view ${view.name}`} className="text-gray-300 hover:text-rose-500 p-0.5"><X className="w-3 h-3" /></button>
              </span>
            ))}
          </div>
        )}

        {filtersOpen && (
            <div className="mt-3 flex flex-wrap gap-4 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
            <FilterGroup title="Date range" dot="bg-sky-400">
              <FilterField icon={Calendar} label="From" active={!!fFrom} chevron={false}><input type="date" value={fFrom} onChange={(e) => setFFrom(e.target.value)} data-testid="scenario-from" className={`${FILTER_INPUT_CLS} pr-0`} /></FilterField>
              <FilterField icon={Calendar} label="To" active={!!fTo} chevron={false}><input type="date" value={fTo} onChange={(e) => setFTo(e.target.value)} data-testid="scenario-to" className={`${FILTER_INPUT_CLS} pr-0`} /></FilterField>
            </FilterGroup>
            <FilterGroup title="Classification" hint="type › event › trigger" dot="bg-indigo-400" grow={1.6}>
              <FilterField icon={Tag} label="Incident type" active={typeF !== 'all'}>
                <SearchSelect value={typeF} onChange={onTypeChange} options={typeOptions} allLabel="All types" testId="scenario-type-filter" />
              </FilterField>
              <FilterField icon={Layers} label="Event" active={eventF !== 'all'} muted={eventOptions.length === 0}>
                <SearchSelect value={eventF} onChange={onEventChange} options={eventOptions} allLabel={eventOptions.length ? 'All events' : 'No events'} disabled={eventOptions.length === 0} testId="scenario-event-filter" />
              </FilterField>
              <FilterField icon={Zap} label="Trigger" active={triggerF !== 'all'} muted={triggerOptions.length === 0}>
                <SearchSelect value={triggerF} onChange={setTriggerF} options={triggerOptions} allLabel={triggerOptions.length ? 'All triggers' : 'No triggers'} disabled={triggerOptions.length === 0} testId="scenario-trigger-filter" />
              </FilterField>
            </FilterGroup>
            <FilterGroup title="Context" dot="bg-emerald-400" grow={1.2}>
              <FilterField icon={MapPin} label="Location" active={locationF !== 'all'}>
                <SearchSelect value={locationF} onChange={setLocationF} options={locationOptions} allLabel="All locations" testId="scenario-location-filter" />
              </FilterField>
              <FilterField icon={Gauge} label="Severity" active={severityF !== 'all'}>
                <select value={severityF} onChange={(e) => setSeverityF(e.target.value)} data-testid="scenario-severity-filter" className={FILTER_INPUT_CLS}>
                  <option value="all">All severities</option>
                  {severityOptions.map((o) => <option key={o.value} value={o.value}>{o.value} ({o.count})</option>)}
                </select>
              </FilterField>
            </FilterGroup>
          </div>
        )}

        {filterChips.length > 0 && (
            <div className="mt-3 flex flex-wrap items-center gap-1.5 rounded-xl border border-indigo-100 bg-indigo-50/60 px-4 py-2.5" data-testid="scenario-filter-chips">
            <span className="text-[10px] font-black uppercase tracking-widest text-indigo-300 mr-1">Active</span>
            {filterChips.map((chip) => (
                <button type="button" key={chip.key} onClick={chip.clear} title="Remove filter"
                className="group flex items-center gap-1.5 text-[11px] font-bold text-indigo-700 bg-white border border-indigo-200 hover:border-rose-200 hover:text-rose-600 px-2.5 py-1 rounded-full transition-colors shadow-sm">
                {chip.label}
                <X className="w-3 h-3 text-indigo-300 group-hover:text-rose-400" />
              </button>
            ))}
          </div>
        )}
      </div>

      {/* List */}
      {loading ? (
        <p className="text-center text-gray-400 py-12">Loading scenarios…</p>
      ) : filtered.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-slate-300 bg-white py-16 text-center" data-testid="scenario-empty">
          <Library className="h-10 w-10 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500 font-medium">{canCreateScenario ? 'No scenarios yet' : 'No assigned scenarios'}</p>
          <p className="text-sm text-gray-400 mb-4">{canCreateScenario ? 'Create your first reusable exercise scenario.' : 'A scenario will appear here when your configured role is assigned to its template.'}</p>
          {canCreateScenario && <Button onClick={openCreate} variant="outline"><Plus className="h-4 w-4 mr-1" /> Create Scenario</Button>}
        </div>
      ) : (
          <div className="grid grid-cols-1 items-stretch gap-5 lg:grid-cols-2 xl:grid-cols-3" data-testid="scenario-list">
          {filtered.map((s) => (
            <ScenarioCard
              key={s.id}
              s={s}
              st={stats[s.id]}
              onOpen={() => navigate(`/scenario-library/${s.id}`)}
              onView={() => setViewItem(s)}
              onEdit={() => openEdit(s)}
              onDuplicate={() => handleDuplicate(s)}
              onArchive={() => handleArchive(s)}
              onDelete={() => handleDelete(s)}
              onLaunch={() => handleLaunch(s)}
              onActivate={() => handleActivateScenario(s)}
              launching={launchingId === s.id}
              activating={activatingId === s.id}
              marketplacePolicy={marketplacePolicy}
              marketplaceListing={marketplacePublications.find((row) => row.source_scenario_id === s.id && ['pending', 'published'].includes(row.status))}
              canPublish={canPublishAtInstitutionLevel}
              permissionLoading={false}
              onPublish={() => handlePublish(s)}
              onUnpublish={() => handleUnpublish(s)}
            />
          ))}
        </div>
      )}

      {wizardOpen && (
        <ScenarioWizard
          existing={editScenario}
          prefill={createPrefill}
          onClose={() => { setWizardOpen(false); setCreatePrefill(null); }}
          onSaved={() => { setWizardOpen(false); setCreatePrefill(null); load(); }}
        />
      )}

      {viewItem && <ScenarioViewModal s={viewItem} st={stats[viewItem.id]} onClose={() => setViewItem(null)} />}
      </div>
    </div>
  );
}

const Kpi = ({ label, value, tone = 'default', testid }) => {
  const tones = {
    default: 'text-slate-950',
    muted: 'text-slate-300',
    accent: 'text-indigo-600',
  };
  return (
    <div className="min-h-[104px] border-b border-r border-slate-100 px-5 py-5 last:border-r-0 sm:px-6 xl:border-b-0" data-testid={testid}>
      <div className={`text-[28px] font-black leading-none tracking-[-0.04em] ${tones[tone] || tones.default}`}>{value}</div>
      <div className="mt-2 text-[11px] font-black uppercase leading-tight tracking-[0.11em] text-slate-400">{label}</div>
    </div>
  );
};


const ScenarioCard = ({ s, st, onOpen, onView, onEdit, onDuplicate, onArchive, onDelete, onLaunch, onActivate, launching, activating, marketplacePolicy, marketplaceListing, canPublish, permissionLoading, onCheckPermissions, onPublish, onUnpublish }) => {
  const stop = (fn) => (e) => { e.stopPropagation(); fn(); };
  const uses = (st?.real_uses || 0) + (st?.drill_uses || 0);
  return (
  <article
    onClick={onOpen}
    onKeyDown={(event) => {
      if (event.target !== event.currentTarget) return;
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        onOpen();
      }
    }}
    role="link"
    tabIndex={0}
    aria-label={`Open scenario ${s.name}`}
    className="group flex min-w-0 cursor-pointer flex-col rounded-2xl border border-slate-200 bg-white p-5 shadow-sm transition duration-200 hover:-translate-y-0.5 hover:border-indigo-200 hover:shadow-xl hover:shadow-slate-200/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 sm:p-6"
    data-testid="scenario-card"
  >
    {/* Header: title + level + actions menu */}
    <div className="mb-4 flex min-h-[70px] items-start justify-between gap-3">
      <div className="min-w-0">
        <h3 className="line-clamp-2 text-[18px] font-black leading-snug tracking-[-0.02em] text-slate-950" dir="auto">{s.name}</h3>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <Badge className={`border px-2.5 py-1 text-[11px] font-bold ${levelColor(s.level)}`}>{s.level}</Badge>
          <span className="inline-flex items-center gap-1.5 text-[12px] font-bold text-slate-600">
            <span className={`h-1.5 w-1.5 rounded-full ${statusDot(s.status)}`} />
            {s.status}
          </span>
        </div>
      </div>
      <DropdownMenu onOpenChange={(open) => { if (open) onCheckPermissions?.(); }}>
        <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
          <button
            type="button"
            data-testid="scenario-actions-menu"
            aria-label={`Actions for ${s.name}`}
            className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
          >
            <MoreVertical className="h-4 w-4" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
          <DropdownMenuItem data-testid="scenario-detail-btn" onClick={stop(onView)}><Eye className="h-4 w-4 mr-2" /> Detail</DropdownMenuItem>
          <DropdownMenuItem data-testid="scenario-open-btn" onClick={stop(onOpen)}><ExternalLink className="h-4 w-4 mr-2" /> Open Scenario</DropdownMenuItem>
          <DropdownMenuItem data-testid="scenario-edit-btn" onClick={stop(onEdit)}><Pencil className="h-4 w-4 mr-2" /> Edit</DropdownMenuItem>
          <DropdownMenuItem data-testid="scenario-duplicate-btn" onClick={stop(onDuplicate)}><Copy className="h-4 w-4 mr-2" /> Duplicate</DropdownMenuItem>
          <DropdownMenuItem data-testid="scenario-archive-btn" onClick={stop(onArchive)}><Archive className="h-4 w-4 mr-2" /> {s.status === 'Archived' ? 'Reactivate' : 'Archive'}</DropdownMenuItem>
          {marketplacePolicy?.can_publish !== false && permissionLoading && (
            <DropdownMenuItem disabled>Checking Marketplace permission…</DropdownMenuItem>
          )}
          {marketplacePolicy?.can_publish !== false && canPublish && <>
            <DropdownMenuSeparator />
            {marketplaceListing ? (
              <DropdownMenuItem data-testid="scenario-unpublish-btn" onClick={stop(onUnpublish)}><Store className="h-4 w-4 mr-2" /> Revoke Marketplace {marketplaceListing.status}</DropdownMenuItem>
            ) : (
              <DropdownMenuItem data-testid="scenario-publish-btn" onClick={stop(onPublish)}><Store className="h-4 w-4 mr-2" /> Submit to Marketplace</DropdownMenuItem>
            )}
          </>}
          <DropdownMenuSeparator />
          <DropdownMenuItem data-testid="scenario-delete-btn" onClick={stop(onDelete)} className="text-red-600 focus:text-red-600"><Trash2 className="h-4 w-4 mr-2" /> Delete</DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>

    <div className="mb-4 min-h-[50px]">
      {s.description && (
        <p className="line-clamp-2 text-[13px] font-medium leading-[1.65] text-slate-500" dir="auto">
          {s.description}
        </p>
      )}
    </div>

    {/* Classification + provenance chips */}
    <div className="mb-4 flex min-h-[30px] flex-wrap items-center gap-1.5">
      {s.incident_type && <span className="max-w-full truncate rounded-lg bg-slate-100 px-2.5 py-1 text-[11px] font-bold text-slate-600" data-testid="chip-type">{s.incident_type}</span>}
      {s.event && <span className="max-w-full truncate rounded-lg bg-blue-50 px-2.5 py-1 text-[11px] font-bold text-blue-700" data-testid="chip-event">{s.event}</span>}
      {s.trigger && <span className="max-w-full truncate rounded-lg bg-cyan-50 px-2.5 py-1 text-[11px] font-bold text-cyan-700" data-testid="chip-trigger">{s.trigger}</span>}
      {s.severity && <span className={`rounded-lg px-2.5 py-1 text-[11px] font-bold ${sevColor(s.severity)}`}>{s.severity}</span>}
      {s.origin === 'ai_agent' && (
        <span className="inline-flex items-center gap-1 rounded-lg border border-indigo-100 bg-indigo-50 px-2.5 py-1 text-[11px] font-bold text-indigo-700" data-testid="chip-ai">
          <Sparkles className="h-3 w-3" /> AI-generated
        </span>
      )}
      {s.marketplace_listing_id && (
        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-amber-50 border border-amber-200 text-amber-700 text-[11px] font-bold" data-testid="chip-marketplace">
          <Store className="h-3 w-3" /> Marketplace
        </span>
      )}
      {st?.gis && (
        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-emerald-50 border border-emerald-200 text-emerald-700 text-[11px] font-bold" data-testid="chip-gis">
          <Globe className="h-3 w-3" /> GIS
        </span>
      )}
      {st?.adherence != null && (
        <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-md border text-[11px] font-bold ${st.adherence >= 80 ? 'bg-emerald-50 border-emerald-200 text-emerald-700' : st.adherence >= 50 ? 'bg-amber-50 border-amber-200 text-amber-700' : 'bg-rose-50 border-rose-200 text-rose-700'}`} data-testid="chip-adherence">
          <Gauge className="h-3 w-3" /> {st.adherence}% adherence
        </span>
      )}
      {uses > 0 && (
        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-slate-100 border border-slate-200 text-slate-600 text-[11px] font-bold" data-testid="chip-uses">
          <History className="h-3 w-3" /> Used {uses}×
        </span>
      )}
    </div>

    {/* Location */}
    <div className="mb-4 flex min-w-0 items-center gap-2 text-[13px] font-medium text-slate-600">
      <MapPin className="h-4 w-4 flex-shrink-0 text-slate-400" />
      <span className="truncate" dir="auto" title={(s.location_names || []).join(', ')}>{(s.location_names || []).join(', ') || 'No location set'}</span>
    </div>

    {/* Real content counts (from the backing incident) */}
    <div className="mb-5 flex min-h-[54px] flex-wrap content-start items-center gap-x-4 gap-y-2 border-b border-slate-100 pb-5 text-[12px] text-slate-500">
      <CardStat icon={Building} value={st?.depts ?? (s.impacted_departments || []).length} label="Depts" />
      <CardStat icon={Boxes} value={st?.resources ?? 0} label="Resources" />
      <CardStat icon={Target} value={st?.roles ?? 0} label="Roles" />
      <CardStat icon={Users} value={st?.personnel ?? 0} label="Personnel" />
      <CardStat icon={Zap} value={st?.injects ?? (s.injected_action_count || 0)} label="Injects" />
    </div>

    {/* Start a drill from an active template, or activate a draft template. */}
    <div className="mt-auto grid grid-cols-2 gap-3">
      <button
        type="button"
        data-testid="scenario-launch-btn"
        onClick={stop(onLaunch)}
        disabled={launching || activating || s.status !== 'Active'}
        title={s.status !== 'Active' ? 'Activate this scenario before starting a drill' : 'Create a Requested drill from this scenario'}
        className="flex h-11 w-full items-center justify-center gap-2 rounded-xl bg-emerald-600 text-[13px] font-bold text-white shadow-sm transition hover:bg-emerald-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:bg-emerald-200 disabled:text-white disabled:opacity-100"
      >
        <Rocket className="h-4 w-4" /> {launching ? 'Starting…' : 'Start Drill'}
      </button>
      <button
        type="button"
        data-testid="scenario-activate-btn"
        onClick={stop(onActivate)}
        disabled={launching || activating || s.status !== 'Draft'}
        title={s.status === 'Draft'
          ? 'Activate this scenario for drill use and real-incident matching'
          : s.status === 'Active'
            ? 'This scenario is active'
            : 'Reactivate this archived scenario from the actions menu first'}
        className="flex h-11 w-full items-center justify-center gap-2 rounded-xl border border-indigo-200 bg-white text-[13px] font-bold text-indigo-700 shadow-sm transition hover:bg-indigo-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:text-indigo-300 disabled:opacity-100"
      >
        <Check className="h-4 w-4" />
        {activating ? 'Activating…' : s.status === 'Active' ? 'Scenario Active' : s.status === 'Archived' ? 'Archived' : 'Activate Scenario'}
      </button>
    </div>

    <div className="mt-3 text-[11px] font-medium text-slate-400">
      {s.created_by_name || 'Unknown'} · v{s.version || 1} · {s.updated_at ? new Date(s.updated_at).toLocaleDateString() : '—'}
    </div>
  </article>
  );
};

const sevColor = (s) => ({
  Low: 'bg-yellow-50 text-yellow-700',
  Medium: 'bg-orange-50 text-orange-700',
  High: 'bg-red-50 text-red-700',
  Critical: 'bg-red-50 text-red-700',
}[s] || 'bg-gray-100 text-gray-600');

const statusDot = (s) => ({
  Draft: 'bg-amber-400',
  Active: 'bg-emerald-500',
  Archived: 'bg-gray-400',
}[s] || 'bg-gray-300');

const CardStat = ({ icon: Icon, value, label }) => (
  <div className="flex items-center gap-1.5" data-testid={`scenario-stat-${label.toLowerCase()}`}>
    <Icon className="h-4 w-4 text-slate-300" />
    <span className="font-black text-slate-800">{value}</span>
    <span className="font-medium text-slate-500">{label}</span>
  </div>
);

/* ----------------------------- Create / Edit Wizard ----------------------------- */
const ScenarioWizard = ({ existing, prefill, onClose, onSaved }) => {
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [basic, setBasic] = useState({
    name: existing?.name || prefill?.name || '',
    description: existing?.description || prefill?.description || '',
    level: existing?.level || prefill?.level || 'Tactical',
    status: existing?.status || 'Draft',
  });
  const [classification, setClassification] = useState(null);
  const [objective, setObjective] = useState({
    title: existing?.simulation_objective?.title || prefill?.objective?.title || '',
    description: existing?.simulation_objective?.description || prefill?.objective?.description || '',
    success_criteria: existing?.simulation_objective?.success_criteria || prefill?.objective?.success_criteria || '',
  });

  // Seed the editor with existing classification (edit) or prefill (from Plan Exercise)
  const editorIncident = useMemo(() => {
    const seed = existing || prefill || {};
    return {
      type_id: seed.type_id || '',
      event_id: seed.event_id || '',
      trigger_id: seed.trigger_id || '',
      exercise_classification: seed.exercise_classification || '',
      location: seed.location || [],
      is_unknown_location: seed.is_unknown_location || false,
      impacted_departments: seed.impacted_departments || [],
      severity: seed.severity || '',
    };
  }, [existing, prefill]);

  const buildPayload = () => {
    const p = {
      name: basic.name,
      description: basic.description,
      level: basic.level,
      ...(classification || {}),
      assumptions: [],
    };
    if (!existing) p.status = 'Draft';
    // Keep the ForeSight lineage when this scenario came from a risk assessment.
    if (!existing && prefill?.source_warning_id) {
      p.source_warning_id = prefill.source_warning_id;
      p.source_assessment_id = prefill.source_assessment_id || null;
    }
    return p;
  };

  const save = async () => {
    if (!basic.name.trim()) { toast.error('Scenario name is required'); setStep(1); return; }
    setSaving(true);
    try {
      const payload = buildPayload();
      const res = existing
        ? await CommanServices.updateScenario(existing.id, payload)
        : await CommanServices.createScenario(payload);
      if (res?.status !== false) {
        const incId = res?.data?.incident_id || existing?.incident_id;
        const hasObj = objective.title?.trim() || objective.description?.trim() || objective.success_criteria?.trim();
        if (incId && hasObj) {
          try {
            await CommanServices.updatePatchIncidents(incId, {
              simulation_objective: {
                title: (objective.title || '').trim(),
                description: (objective.description || '').trim(),
                success_criteria: (objective.success_criteria || '').trim(),
              },
            });
          } catch (e) { /* objective is optional */ }
        }
        toast.success(existing ? 'Scenario updated' : 'Scenario saved as Draft — open it to design the simulation');
        onSaved();
      } else {
        toast.error(res?.message || 'Failed to save scenario');
      }
    } catch (e) {
      console.error('save scenario failed', e);
      toast.error('Failed to save scenario');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="csw-font csw-scroll max-h-[calc(100vh-2rem)] max-w-[680px] gap-0 overflow-y-auto rounded-2xl border-0 p-0 [&>button]:hidden"
        style={{
          background: 'linear-gradient(180deg,#fff,#fcfcff)',
          boxShadow: '0 1px 0 rgba(255,255,255,0.9) inset, 0 24px 60px rgba(10,15,45,0.28), 0 60px 140px rgba(10,15,45,0.3)',
        }}
        data-testid="scenario-wizard"
      >
        <style>{CSW_STYLE}</style>
        {/* Top edge gradient strip */}
        <div
          className="pointer-events-none absolute inset-x-0 top-0 h-[3px]"
          style={{ background: 'linear-gradient(90deg, transparent, #7c3aed 30%, #4338ca 70%, transparent)', opacity: 0.55 }}
        />
        <DialogHeader className="px-5 pb-3 pt-4 sm:px-6" style={{ background: 'linear-gradient(90deg, rgba(124,58,237,0.06), rgba(67,56,202,0.02))', borderBottom: '1px solid #edf0f9' }}>
          <div className="flex items-center gap-3">
            <div
              className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl"
              style={{ background: 'linear-gradient(135deg,#4338ca,#7c3aed)', boxShadow: '0 1px 0 rgba(255,255,255,0.25) inset, 0 8px 24px rgba(79,70,229,0.35)' }}
            >
              <Layers className="h-[18px] w-[18px] text-white" />
            </div>
            <div className="flex-1 text-left">
              <DialogTitle className="text-[18px] font-bold text-[#0f172a]">{existing ? 'Edit Scenario' : 'Create Scenario'}</DialogTitle>
              <p className="mt-0.5 text-[12px] leading-snug text-[#64748b]">Build a reusable exercise scenario — classification, location, departments and objective.</p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full transition-colors hover:text-[#0f172a]"
              style={{ background: '#f5f6fb', border: '1px solid #e9ecf6', color: '#64748b' }}
              data-testid="wizard-close"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </DialogHeader>

        {/* Stepper */}
        <div className="flex items-center gap-2.5 px-5 py-3 sm:px-6" style={{ borderBottom: '1px solid #edf0f9' }}>
          {['Basic & Classification', 'Objective'].map((label, i) => {
            const n = i + 1;
            const activeStep = step === n;
            const done = step > n;
            return (
              <React.Fragment key={n}>
                <button
                  type="button"
                  onClick={() => setStep(n)}
                  className="flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[12.5px] font-bold transition-all"
                  style={activeStep
                    ? { background: 'linear-gradient(135deg,#4338ca,#7c3aed)', color: '#fff', border: '1px solid transparent',
                        boxShadow: '0 1px 0 rgba(255,255,255,0.25) inset, 0 6px 18px rgba(79,70,229,0.35)' }
                    : done
                      ? { background: '#ecfdf5', border: '1px solid #bbf7d0', color: '#15803d' }
                      : { background: '#fff', border: '1px solid #dbe0f0', color: '#64748b' }}
                  data-testid={`wizard-step-${n}`}
                >
                  <span
                    className="flex h-5 w-5 items-center justify-center rounded-full text-[11px] font-bold"
                    style={activeStep ? { background: 'rgba(255,255,255,0.22)' } : done ? { background: '#bbf7d0' } : { background: '#f1f3fa' }}
                  >
                    {done ? <Check className="h-3 w-3" /> : n}
                  </span>
                  {label}
                </button>
                {n === 1 && <div className="h-[2px] flex-1 rounded-full" style={{ background: 'linear-gradient(90deg,#c7d2fe,#edf0f9)' }} />}
              </React.Fragment>
            );
          })}
        </div>

        <div className="grid gap-4 p-5 sm:px-6">
        {step === 1 && (
          <>
            <div>
              <label className="csw-eyebrow mb-1.5 block">Scenario Name <span style={{ color: '#dc2626' }}>*</span></label>
              <input data-testid="scenario-name-input" value={basic.name} onChange={(e) => setBasic((b) => ({ ...b, name: e.target.value }))}
                placeholder="e.g. HVAC fire — Building SDAIA" className="csw-input w-full px-3.5 py-2 text-sm" />
            </div>
            <div>
              <label className="csw-eyebrow mb-2 block">Scenario Level</label>
              <div className="flex flex-wrap gap-2" data-testid="scenario-level-select">
                {LEVELS.map((l) => (
                  <CswPill key={l} active={basic.level === l} Icon={LEVEL_ICONS[l]} label={l}
                    onClick={() => setBasic((b) => ({ ...b, level: l }))} testId={`scenario-level-pill-${l}`} />
                ))}
              </div>
            </div>
            <div>
              <label className="csw-eyebrow mb-1.5 block">Description</label>
              <input data-testid="scenario-description-input" value={basic.description} onChange={(e) => setBasic((b) => ({ ...b, description: e.target.value }))}
                className="csw-input w-full px-3.5 py-2 text-sm" placeholder="Short summary…" />
            </div>

            <ExerciseScenarioEditor incident={editorIncident} onChange={setClassification} premium />
          </>
        )}

        {step === 2 && (
          <ObjectiveStep objective={objective} setObjective={setObjective} />
        )}
        </div>

        <DialogFooter className="sticky bottom-0 z-10 flex items-center justify-between gap-2 px-5 py-3 sm:px-6" style={{ borderTop: '1px solid #edf0f9', background: '#fafbfe' }}>
          <div>
            {step === 2 && (
              <Button variant="ghost" onClick={() => setStep(1)} data-testid="wizard-back" className="h-9 px-3 text-sm text-[#475569]">
                <ArrowLeft className="mr-1.5 h-4 w-4" /> Back
              </Button>
            )}
          </div>
          <div className="flex items-center gap-1.5" data-testid="wizard-primary-actions">
            <Button variant="ghost" onClick={onClose} data-testid="wizard-cancel" className="h-9 px-3 text-sm text-[#475569]">Cancel</Button>
            {step === 1 ? (
              <button onClick={() => setStep(2)} className="csw-primary flex h-9 items-center gap-1.5 rounded-lg px-4 text-sm font-bold" data-testid="wizard-next">
                Next <ChevronRight className="h-4 w-4" />
              </button>
            ) : (
              <button onClick={save} disabled={saving} className="csw-primary flex h-9 items-center gap-1.5 rounded-lg px-4 text-sm font-bold disabled:opacity-60" data-testid="save-scenario-btn">
                <Save className="h-4 w-4" /> {saving ? 'Saving…' : 'Save'}
              </button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const ObjectiveStep = ({ objective, setObjective }) => {
  const set = (k, v) => setObjective((o) => ({ ...o, [k]: v }));
  return (
    <div className="space-y-4" data-testid="objective-step">
      <div
        className="rounded-2xl p-[22px]"
        style={{
          background: 'linear-gradient(180deg,#fbfbff,#f6f5fe)',
          border: '1px solid #e5e4f8',
          boxShadow: '0 1px 0 rgba(255,255,255,0.9) inset, 0 10px 30px rgba(124,58,237,0.08)',
        }}
      >
        <div className="mb-4 flex items-center gap-3">
          <span
            className="flex h-[42px] w-[42px] items-center justify-center rounded-xl text-white"
            style={{ background: 'linear-gradient(135deg,#7c3aed,#a855f7)', boxShadow: '0 6px 18px rgba(124,58,237,0.35)' }}
          >
            <Target className="h-5 w-5" />
          </span>
          <div>
            <h4 className="text-[16.5px] font-bold text-[#0f172a]">Simulation / Drill Objective</h4>
            <p className="text-[12.5px] text-[#64748b]">What this exercise aims to validate.</p>
          </div>
        </div>
        <div className="space-y-4">
          <div>
            <label className="csw-eyebrow mb-1.5 block">Title</label>
            <input data-testid="objective-title" value={objective.title} onChange={(e) => set('title', e.target.value)}
              placeholder="e.g. Test fire evacuation readiness for Building SADIA" className="csw-input w-full px-3.5 py-2.5 text-sm" />
          </div>
          <div>
            <label className="csw-eyebrow mb-1.5 block">Description</label>
            <textarea data-testid="objective-description" rows={4} value={objective.description} onChange={(e) => set('description', e.target.value)}
              placeholder="Describe the scenario goals, scope and what participants should accomplish…"
              className="csw-input w-full resize-y px-3.5 py-2.5 text-sm" />
          </div>
          <div>
            <label className="csw-eyebrow mb-1.5 flex items-center gap-1.5"><ListChecks className="h-3.5 w-3.5" /> Success Criteria <span className="font-normal normal-case tracking-normal text-[#a3abc4]">(optional)</span></label>
            <textarea data-testid="objective-success" rows={3} value={objective.success_criteria} onChange={(e) => set('success_criteria', e.target.value)}
              placeholder="How will success be measured? e.g. Full evacuation under 8 minutes…"
              className="csw-input w-full resize-y px-3.5 py-2.5 text-sm" />
          </div>
        </div>
      </div>
      <p className="flex items-center gap-1.5 text-[12.5px]" style={{ color: '#94a3b8' }}>
        <Info className="h-3.5 w-3.5 flex-shrink-0" />
        <span>Saved as a <b style={{ color: '#64748b' }}>Draft</b>. Open the scenario afterwards to design the timeline, discussion group and injects, then <b style={{ color: '#64748b' }}>Save</b>.</span>
      </p>
    </div>
  );
};

/* ----------------------------- View Modal ----------------------------- */
const ScenarioViewModal = ({ s, st, onClose }) => {
  const [usage, setUsage] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await CommanServices.getScenarioUsage(s.id);
        if (!cancelled) setUsage(res?.data?.usage || []);
      } catch (e) { if (!cancelled) setUsage([]); }
    })();
    return () => { cancelled = true; };
  }, [s.id]);
  return (
  <Dialog open onOpenChange={(o) => !o && onClose()}>
    <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="scenario-view-modal">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          {s.name}
          <Badge className={`border text-[10px] ${levelColor(s.level)}`}>{s.level}</Badge>
          <Badge className={`border text-[10px] ${statusColor(s.status)}`}>{s.status}</Badge>
        </DialogTitle>
      </DialogHeader>
      {s.description && <p className="text-sm text-gray-600">{s.description}</p>}
      <div className="grid grid-cols-2 gap-2 text-sm">
        <Detail k="Incident Type" v={s.incident_type} />
        <Detail k="Event" v={s.event} />
        <Detail k="Trigger" v={s.trigger} />
        <Detail k="Severity" v={s.severity} />
        <Detail k="Exercise Classification" v={s.exercise_classification} />
        <Detail k="Location" v={(s.location_names || []).join(', ')} />
        <Detail k="Impacted Departments" v={`${st?.depts ?? (s.impacted_departments || []).length}`} />
        <Detail k="Version" v={`v${s.version || 1}`} />
        <Detail k="Injects" v={`${st?.injects ?? 0}`} />
        <Detail k="Resources" v={`${st?.resources ?? 0}`} />
        <Detail k="Roles / Personnel" v={`${st?.roles ?? 0} / ${st?.personnel ?? 0}`} />
        <Detail k="Adherence" v={st?.adherence != null ? `${st.adherence}%` : '—'} />
        <Detail k="Incident uses" v={`${st?.real_uses ?? 0}`} />
        <Detail k="Drill uses" v={`${st?.drill_uses ?? 0}`} />
      </div>

      {(s.origin === 'ai_agent' || s.marketplace_listing_id || st?.gis) && (
        <div className="flex flex-wrap gap-1.5" data-testid="scenario-view-chips">
          {s.origin === 'ai_agent' && <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-violet-50 border border-violet-200 text-violet-700 text-[11px] font-bold"><Sparkles className="h-3 w-3" /> AI-generated</span>}
          {s.marketplace_listing_id && <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-amber-50 border border-amber-200 text-amber-700 text-[11px] font-bold"><Store className="h-3 w-3" /> From Marketplace</span>}
          {st?.gis && <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-emerald-50 border border-emerald-200 text-emerald-700 text-[11px] font-bold"><Globe className="h-3 w-3" /> GIS used</span>}
        </div>
      )}

      <div>
        <h4 className="text-sm font-bold text-gray-800 mt-2 mb-1 flex items-center gap-1"><Rocket className="h-3.5 w-3.5" /> Usage History</h4>
        <div className="space-y-1 max-h-44 overflow-y-auto" data-testid="scenario-usage-history">
          {usage === null && <p className="text-xs text-gray-400 italic">Loading usage…</p>}
          {usage !== null && usage.length === 0 && <p className="text-xs text-gray-400 italic">Never used yet — launch it as a drill or let a matching real incident activate it.</p>}
          {(usage || []).map((u) => (
            <div key={u.incident_id} className="flex items-center justify-between gap-2 border-b border-gray-50 py-1.5 text-[11px]" data-testid={`usage-row-${u.incident_id}`}>
              <span className="flex items-center gap-1.5 min-w-0">
                <span className={`px-1.5 py-0.5 rounded font-bold uppercase text-[9px] ${u.kind === 'real' ? 'bg-rose-100 text-rose-700' : 'bg-sky-100 text-sky-700'}`}>
                  {u.kind === 'real' ? 'Real incident' : 'Drill'}
                </span>
                <span className="truncate font-medium text-gray-700" title={u.title}>{u.title}</span>
              </span>
              <span className="flex items-center gap-2 flex-shrink-0 text-gray-500">
                {u.adherence != null && (
                  <span className={`font-bold ${u.adherence >= 80 ? 'text-emerald-600' : u.adherence >= 50 ? 'text-amber-600' : 'text-rose-600'}`}>
                    {u.adherence}% ({u.sla_met}/{u.sla_tracked})
                  </span>
                )}
                <span>{u.used_at ? new Date(u.used_at).toLocaleString() : ''}</span>
              </span>
            </div>
          ))}
        </div>
      </div>

      <div>
        <h4 className="text-sm font-bold text-gray-800 mt-2 mb-1 flex items-center gap-1"><Target className="h-3.5 w-3.5" /> Simulation Objective</h4>
        {(() => {
          const obj = s.simulation_objective || {};
          const hasObj = obj.title || obj.description || obj.success_criteria;
          if (!hasObj) return <p className="text-xs text-gray-400 italic">No objective defined</p>;
          return (
            <div className="border border-gray-200 rounded p-2.5 text-xs space-y-1.5" data-testid="scenario-objective">
              {obj.title && <div className="font-semibold text-gray-800">{obj.title}</div>}
              {obj.description && <p className="text-gray-600">{obj.description}</p>}
              {obj.success_criteria && (
                <div className="text-gray-600"><span className="font-semibold text-gray-500">Success criteria: </span>{obj.success_criteria}</div>
              )}
            </div>
          );
        })()}
      </div>

      <div>
        <h4 className="text-sm font-bold text-gray-800 mt-2 mb-1 flex items-center gap-1"><History className="h-3.5 w-3.5" /> Audit Log</h4>
        <div className="space-y-1 max-h-40 overflow-y-auto" data-testid="scenario-audit-log">
          {(s.audit_log || []).slice().reverse().map((e, i) => (
            <div key={i} className="text-[11px] text-gray-500 flex justify-between border-b border-gray-50 py-1">
              <span><span className="font-semibold capitalize">{e.action}</span> by {e.by_name || 'User'}{e.changes?.length ? ` · ${e.changes.length} field(s)` : ''}</span>
              <span>{e.at ? new Date(e.at).toLocaleString() : ''}</span>
            </div>
          ))}
          {(!s.audit_log || s.audit_log.length === 0) && <p className="text-xs text-gray-400 italic">No history</p>}
        </div>
      </div>

      <DialogFooter>
        <Button variant="outline" onClick={onClose}>Close</Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
  );
};

const Detail = ({ k, v }) => (
  <div><span className="text-gray-400 text-xs">{k}:</span> <span className="text-gray-800">{v || '—'}</span></div>
);
